import React, { useState, useEffect } from 'react';
import { Constants } from '@eagerdog/constants';
import { Modal } from '../Modal/Modal';
import Button from '../Button/Button';
import Toggle from '../Toggle/Toggle';
import { toast } from '../Toast/ToastManager';
import { apiService } from 'src/services';
import { helperService } from 'src/services/helper.service';

import './EditCapacitiesModal.scss';

interface IProps {
  clubId: string;
  user: {
    user_id: string;
    name: string;
    email: string;
    currentCapacities: string[];
  };
  isShown: boolean;
  hide: () => void;
  onSave?: () => void;
}

const EditCapacitiesModal: React.FC<IProps> = ({ clubId, user, isShown, hide, onSave }) => {
  const [hasParticipant, setHasParticipant] = useState<boolean>(false);
  const [hasVolunteer, setHasVolunteer] = useState<boolean>(false);
  const [hasMember, setHasMember] = useState<boolean>(false);
  const [hasAdmin, setHasAdmin] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Reset toggles when modal opens or user changes
  useEffect(() => {
    if (isShown && user) {
      setHasParticipant(user.currentCapacities.includes(Constants.capacity_type.participant));
      setHasVolunteer(user.currentCapacities.includes(Constants.capacity_type.volunteer));
      setHasMember(user.currentCapacities.includes(Constants.capacity_type.member));
      setHasAdmin(user.currentCapacities.includes(Constants.capacity_type.admin));
    }
  }, [isShown, user]);

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Determine which capacities to add/remove
      const currentCapacities = user.currentCapacities;
      const newCapacities: string[] = [];
      
      if (hasParticipant) newCapacities.push(Constants.capacity_type.participant);
      if (hasVolunteer) newCapacities.push(Constants.capacity_type.volunteer);
      if (hasMember) newCapacities.push(Constants.capacity_type.member);
      if (hasAdmin) newCapacities.push(Constants.capacity_type.admin);

      const capacitiesToAdd = newCapacities.filter(c => !currentCapacities.includes(c));
      const capacitiesToRemove = currentCapacities.filter(c => !newCapacities.includes(c));

      // Get current logged-in user
      const currentUser = helperService.getUser();

      // Check if user is trying to remove their own admin capacity
      if (capacitiesToRemove.includes(Constants.capacity_type.admin) && currentUser._id === user.user_id) {
        toast.show({
          title: 'Cannot Remove',
          content: 'You cannot remove your own admin access.',
          type: 'fail',
          duration: 5000
        });
        setIsSaving(false);
        return;
      }

      // Perform all add operations
      await Promise.all(
        capacitiesToAdd.map(capacity =>
          apiService.addClubCapacity(clubId, user.user_id, capacity)
        )
      );

      // Perform all remove operations
      await Promise.all(
        capacitiesToRemove.map(capacity =>
          apiService.removeClubCapacity(clubId, user.user_id, capacity)
        )
      );

      toast.show({
        title: 'Success',
        content: `Roles updated for ${user.name}`,
        type: 'success',
        duration: 3000
      });

      if (onSave) {
        onSave();
      }
      
      hide();
    } catch (error) {
      console.error('Error updating capacities:', error);
      toast.show({
        title: 'Error',
        content: 'Failed to update roles',
        type: 'fail',
        duration: 5000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = () => {
    return (
      <div className="EditCapacitiesModal">
        <div className="modalHeader">
          <h2>Edit Member Roles</h2>
          <div className="userInfo">
            <div className="userName">{user.name}</div>
            <div className="userEmail">{user.email}</div>
          </div>
        </div>
        
        <div className="capacitiesContent">
          <div className="capacityItem">
            <div className="capacityInfo">
              <label className="capacityLabel">Participant</label>
              <p className="capacityDescription">
                Can participate in events and register dogs
              </p>
            </div>
            <div className="capacityControl">
              <Toggle
                id="capacity-participant"
                checked={hasParticipant}
                onChange={(checked) => setHasParticipant(checked)}
              />
            </div>
          </div>

          <div className="capacityItem">
            <div className="capacityInfo">
              <label className="capacityLabel">Volunteer</label>
              <p className="capacityDescription">
                Can access volunteer features and early/late registration (if enabled)
              </p>
            </div>
            <div className="capacityControl">
              <Toggle
                id="capacity-volunteer"
                checked={hasVolunteer}
                onChange={(checked) => setHasVolunteer(checked)}
              />
            </div>
          </div>

          <div className="capacityItem">
            <div className="capacityInfo">
              <label className="capacityLabel">Member</label>
              <p className="capacityDescription">
                Club member with access to member features
              </p>
            </div>
            <div className="capacityControl">
              <Toggle
                id="capacity-member"
                checked={hasMember}
                onChange={(checked) => setHasMember(checked)}
              />
            </div>
          </div>

          <div className="capacityItem">
            <div className="capacityInfo">
              <label className="capacityLabel">Admin</label>
              <p className="capacityDescription">
                Full administrative access to manage the club and events
              </p>
            </div>
            <div className="capacityControl">
              <Toggle
                id="capacity-admin"
                checked={hasAdmin}
                onChange={(checked) => setHasAdmin(checked)}
              />
            </div>
          </div>
        </div>
        
        <div className="modalActions">
          <Button
            variant="secondary"
            onClick={hide}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
          >
            Save Changes
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      className="edit-capacities-modal"
      isShown={isShown}
      hide={hide}
      modalContent={modalContent()}
    />
  );
};

export default EditCapacitiesModal;

